import { useEditor } from '@tiptap/react';
import { createContext, useMemo, useRef, useEffect } from 'react';

import { type ControlsInput } from '../types';
import { getExtensions } from '../utils';

import type { RichTextEditorContextData, RichTextEditorProviderProps } from './types';

const RichTextEditorContext = createContext<RichTextEditorContextData | undefined>(undefined);

function RichTextEditorProvider(props: Readonly<RichTextEditorProviderProps>) {
  const { children, content = '', onImageUpload, placeholder, onChange, controls } = props;

  const lastContentRef = useRef<string>(content);

  const extensions = useMemo(
    () =>
      getExtensions({
        onImageUpload,
        placeholder,
        controls: controls as ControlsInput | undefined,
      }),
    [onImageUpload, placeholder, controls],
  );

  const editor = useEditor({
    extensions,
    content,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      lastContentRef.current = html;
      onChange?.(editor.getHTML());
    },
  });

  useEffect(() => {
    if (!editor) return;
    if (content === lastContentRef.current) return;
    if (content === editor.getHTML()) {
      lastContentRef.current = content;
      return;
    }
    queueMicrotask(() => {
      try {
        editor.commands.setContent(content ?? '');
        lastContentRef.current = content;
      } catch {
        // Silent: avoid throwing during race conditions (editor disposing).
      }
    });
  }, [content, editor]);

  // Destroy safety (optional; Tiptap cleans up but we ensure reference invalidation).
  useEffect(
    () => () => {
      editor?.destroy();
    },
    [editor],
  );

  const contextValue = useMemo(
    () => ({
      editor,
      onImageUpload,
    }),
    [editor, onImageUpload],
  );

  return (
    <RichTextEditorContext.Provider value={contextValue}>{children}</RichTextEditorContext.Provider>
  );
}

export { RichTextEditorContext, RichTextEditorProvider };
